import { useEffect, useState } from "react"
import { Box } from "@mui/material"
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined"
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined"
import { StyledRoot } from "./styled"
import { IRootProps } from "./types"
import { Divider, IconButton, Typography } from "../../../basics"
import { useSidebarStore } from "src/store/sidebarStore"
import { useIsMobile } from "src/lib/hooks/useIsMobile"
import { intl } from "src/internationalization/intl"

interface ISidebarRootProps extends IRootProps {
    children: React.ReactNode
    footer: React.ReactNode
}

const Root = ({ children, footer, ...props }: ISidebarRootProps) => {
    const open = useSidebarStore((s: any) => s.open)
    const toggle = useSidebarStore((s: any) => s.toggle)
    const mobileOpen = useSidebarStore((s: any) => s.mobileOpen)
    const setMobileOpen = useSidebarStore((s: any) => s.setMobileOpen)
    const isMobile = useIsMobile()
    const [now, setNow] = useState<Date>(new Date())

    // no mobile a sidebar é um overlay sempre expandido
    const expanded = isMobile || open

    useEffect(() => {
        if (!expanded) return
        setNow(new Date())
        const intervalId = window.setInterval(() => setNow(new Date()), 60 * 1000)
        return () => window.clearInterval(intervalId)
    }, [expanded])

    const formattedDay = now.toLocaleDateString("pt-BR")
    const formattedTime = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })

    return (
        <StyledRoot
            {...props}
            variant={isMobile ? "temporary" : "permanent"}
            anchor="left"
            open={isMobile ? mobileOpen : true}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            expanded={expanded}
            isMobile={isMobile}
            className="AppSidebarRoot"
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: expanded ? "space-between" : "center",
                    padding: expanded ? "16px" : "16px 0",
                    minHeight: 40,
                    gap: 1,
                }}
            >
                {expanded && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {intl("welcomeText1")}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                color: (theme) => (theme as any).palette.neutral.main,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {`${intl("todayIs")} ${formattedDay}. ${intl("itIsTime")} ${formattedTime}.`}
                        </Typography>
                    </Box>
                )}
                <IconButton
                    variant="clean"
                    color="neutral"
                    onClick={isMobile ? () => setMobileOpen(false) : toggle}
                    aria-label={isMobile ? "Fechar menu" : open ? "Recolher menu" : "Expandir menu"}
                >
                    {expanded ? <ChevronLeftOutlinedIcon fontSize="small" /> : <ChevronRightOutlinedIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "8px" }}>
                {children}
            </Box>
            <Divider />
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 60, padding: "8px", boxSizing: "border-box" }}>
                {footer}
            </Box>
        </StyledRoot>
    )
}

export { Root }
